import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/**
 * A person in the Important Person directory (GET /reference-directory/).
 * Field names are best-effort — the list renders defensively via the helpers below.
 */
export interface ReferencePersonRecord {
  id: number;
  name?: string | null;
  full_name?: string | null;
  department?: string | null;
  designation?: string | null;
  phone?: string | null;
  phones?: string[] | null;
  contact?: string | null;
  email?: string | null;
  location?: string | null;
  status?: string | null;
}

export const refName = (p: ReferencePersonRecord) => p.name || p.full_name || `Person ${p.id}`;
export const refPhone = (p: ReferencePersonRecord) =>
  p.phone || p.contact || (p.phones ?? []).filter(Boolean).join(', ') || '';

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

export interface ReferenceDirectoryResponse {
  items: ReferencePersonRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReferenceDirectoryArgs {
  search?: string;
  department?: string;
  organization?: number;
  page?: number;
  pageSize?: number;
}

export const referenceDirectoryApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getReferenceDirectory: build.query<ReferenceDirectoryResponse, ReferenceDirectoryArgs | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.referenceDirectory}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.department ? { department: arg.department } : {}),
          ...(arg?.organization != null ? { organization: arg.organization } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<ReferencePersonRecord>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['ReferenceDirectory'],
    }),
    getReferenceDirectoryFilters: build.query<{ departments: string[] }, void>({
      query: () => ({ url: `${ApiEndPoints.referenceDirectoryFilters}/` }),
      transformResponse: (r: { departments?: string[] } | null) => ({
        departments: r?.departments ?? [],
      }),
    }),
    deleteReferencePerson: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.referenceDirectory}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['ReferenceDirectory'],
    }),
  }),
});

export const {
  useGetReferenceDirectoryQuery,
  useGetReferenceDirectoryFiltersQuery,
  useDeleteReferencePersonMutation,
} = referenceDirectoryApi;
